import { KTag } from '@/ui-library/components/atoms/KTag';
import { KText } from '@/ui-library/components';
import {
  Flex,
  Box,
  MenuItem,
  Button,
  useDisclosure,
  Icon,
} from '@chakra-ui/react';
import { KMenuButton } from '@/ui-library/components/atoms/KMenuButton/KMenuButton';
import { ChevronDown } from '@kula-ai/icons/outline/ChevronDown';
import { ApprovalFlowSteps } from '@/components/ats/ApprovalFlowSteps';
import {
  useLazyGetApprovalsByEntityQuery,
  useLazyGetApprovalsCompletedByEntityQuery,
} from '@/store/query/approval-builder';
import { requisitionStatusConfig } from '../constants/constants';
import { Requisition } from '../types';
import { GroupRequisitionCodes } from './PartOfGroupTag';
import React, { useEffect, useState } from 'react';
import { useRequisitionActions } from '../hooks/useRequisitionActions';
import {
  RequisitionActionType,
  REQUISITION_ACTIONS,
  REQUISITION_ACTIONS_BY_STATUS,
  REQUISITION_GROUP_ACTIONS,
} from '../constants/requisitionActions';
import useUrlState from '@ahooksjs/use-url-state';

interface RequisitionStatusTagProps {
  status: Requisition['status'];
  requisitionId?: number;
  showApprovalMenu?: boolean;
  showActionMenu?: boolean;
  onApprovalChange?: () => void;
  onStatusChange?: (action: RequisitionActionType) => void;
  requisition?: Requisition;
  openMenuByDefault?: boolean;
  allRequisitions?: Requisition[];
  isPartOfGroup?: boolean;
  allGroupRequisitions?: Requisition[];
}

export const RequisitionStatusTag: React.FC<RequisitionStatusTagProps> = ({
  status,
  requisitionId,
  showApprovalMenu = false,
  showActionMenu = false,
  onApprovalChange,
  onStatusChange,
  requisition = null,
  openMenuByDefault = false,
  allRequisitions = [],
  isPartOfGroup = false,
  allGroupRequisitions = [],
}) => {
  const isPendingApproval = status === 'pending_approval';
  const isRejected = status === 'rejected';
  const shouldShowApprovalMenu =
    (isPendingApproval || isRejected) && showApprovalMenu && requisitionId;
  const shouldShowActionMenu =
    !isPendingApproval && !isRejected && showActionMenu && requisitionId;
  const shouldShowAnyMenu = shouldShowApprovalMenu || shouldShowActionMenu;
  const menuActions = useDisclosure();
  const [hasFetched, setHasFetched] = useState(false);
  const [, setUrlState] = useUrlState();

  const [
    getApprovalData,
    { data: approvalFlow, isFetching: isApprovalLoading },
  ] = useLazyGetApprovalsByEntityQuery();
  const [
    getCompletedApprovalData,
    { data: completedApprovalFlow, isFetching: isCompletedLoading },
  ] = useLazyGetApprovalsCompletedByEntityQuery();

  const isLoading = isApprovalLoading || isCompletedLoading;

  // Get available actions for current status
  const availableActions = REQUISITION_ACTIONS_BY_STATUS[status] || [];
  const baseActionMenuItems = availableActions
    .filter((action) => {
      // Only show status transition actions in the menu
      if (!['close', 'open', 'reopen', 'archive'].includes(action)) {
        return false;
      }

      // Skip reopen if requisition has an association (was filled)
      if (action === 'reopen' && requisition?.requisition_association?.id) {
        return false;
      }

      return true;
    })
    .map((actionId) => {
      const action = REQUISITION_ACTIONS[actionId];
      const groupConfig = REQUISITION_GROUP_ACTIONS[actionId];

      // If part of group, use group config for single action label/description
      if (isPartOfGroup && groupConfig) {
        return {
          ...action,
          label: groupConfig.singleLabel,
          description: groupConfig.singleDescription || action.description,
        };
      }
      return action;
    });

  // Create action menu items with "all" versions when part of a group
  const actionMenuItems = React.useMemo(() => {
    if (!isPartOfGroup) {
      return baseActionMenuItems;
    }

    // For each action, create both single and "all" versions
    const items: Array<
      typeof baseActionMenuItems[0] & {
        isAllAction?: boolean;
        baseActionId?: RequisitionActionType;
        groupRequisitionCodes?: string[];
      }
    > = [];

    baseActionMenuItems.forEach((action) => {
      const groupConfig = REQUISITION_GROUP_ACTIONS[action.id];

      // Add single action (already updated with group config labels)
      // For close action, use close_single when part of a group
      const singleAction = {
        ...action,
        ...(action.id === 'close' ? { id: 'close_single' as any } : {}),
      };
      items.push(singleAction);

      // Add "all" version with group config - always create if part of group
      if (groupConfig) {
        let filteredGroupReqs = allGroupRequisitions;

        // For open/reopen actions, filter out associated open reqs and filled reqs
        if (action.id === 'open' || action.id === 'reopen') {
          filteredGroupReqs = allGroupRequisitions.filter(
            (req) => req.status !== 'open' && req.status !== 'filled'
          );
        }

        const groupCodes =
          filteredGroupReqs.length > 0
            ? filteredGroupReqs.map((req) => req.code)
            : [];

        items.push({
          ...action,
          id: `${action.id}_all` as any,
          label: groupConfig.groupLabel,
          description: groupConfig.groupDescription,
          isAllAction: true,
          baseActionId: action.id,
          groupRequisitionCodes: groupCodes,
        });
      }
    });

    return items;
  }, [baseActionMenuItems, isPartOfGroup, allGroupRequisitions]);

  const onCloseReqFromMenu = () => {
    if (status === 'draft') {
      setUrlState({ view: requisitionId.toString(), draft: undefined });
    }
  };

  // Use requisition actions hook for action handling
  const { handleAction, renderModals } = useRequisitionActions({
    onRefetch: (action) => {
      onStatusChange?.(action);
    },
    onCloseReqFromMenu,
  });

  // Close requisition menu items for grouped requisitions
  const closeMenuItems = React.useMemo(() => {
    if (!isPartOfGroup || allGroupRequisitions.length === 0) {
      return [];
    }

    return [
      {
        id: 'close_single',
        label: 'Close this requisition alone',
        description:
          'Mark this requisition as closed. Closed requisitions can be reopened or archived.',
        onClick: (e: any) => {
          e.stopPropagation();
          if (requisition) {
            handleAction(
              'close_single',
              requisition,
              menuActions.onClose,
              false,
              []
            );
          }
        },
      },
      {
        id: 'close_all',
        label: 'Close all requisitions in the group',
        description: '',
        additionalInfo: (
          <GroupRequisitionCodes
            codes={allGroupRequisitions.map((req) => req.code)}
            codeStyles={{ maxHeight: '200px', overflowY: 'auto' }}
          />
        ),
        onClick: (e: any) => {
          e.stopPropagation();
          if (requisition) {
            handleAction(
              'close',
              requisition,
              menuActions.onClose,
              true,
              allGroupRequisitions
            );
          }
        },
      },
    ];
  }, [
    isPartOfGroup,
    allGroupRequisitions,
    requisition,
    handleAction,
    menuActions.onClose,
  ]);

  // Open menu by default when openMenuByDefault prop is true
  useEffect(() => {
    if (openMenuByDefault && shouldShowAnyMenu && !menuActions.isOpen) {
      menuActions.onOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMenuByDefault, shouldShowAnyMenu]);

  useEffect(() => {
    if (shouldShowApprovalMenu && menuActions.isOpen && !hasFetched) {
      if (isRejected) {
        getCompletedApprovalData({ id: requisitionId, type: 'requisition' });
      } else if (isPendingApproval) {
        getApprovalData({ id: requisitionId, type: 'requisition' });
      }
      setHasFetched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowApprovalMenu, menuActions.isOpen, requisitionId, hasFetched]);

  const StatusContent = ({ showIcon = false }) => (
    <Flex alignItems="center" gap="6px">
      <Box
        w="8px"
        h="8px"
        bg={requisitionStatusConfig[status]?.color}
        borderRadius="full"
      />
      <KText size="sm" color="secondary-700">
        {requisitionStatusConfig[status]?.label}
      </KText>
      {showIcon && (
        <Icon
          as={ChevronDown}
          height="14px"
          width="14px"
          color="secondary-700"
        />
      )}
    </Flex>
  );

  // If no menu should be shown, return simple tag
  if (
    !shouldShowAnyMenu ||
    (shouldShowActionMenu && actionMenuItems.length === 0)
  ) {
    return (
      <KTag
        color={requisitionStatusConfig[status]?.color}
        bg="white"
        borderColor="border-color"
        borderWidth="1px"
        value={<StatusContent showIcon={false} />}
      />
    );
  }

  // For pending_approval with menu enabled, show menu button
  return (
    <>
      <KMenuButton
        variant="unstyled"
        h="auto"
        minW="auto"
        p="0"
        display="flex"
        paddingInline="0"
        onClick={(e) => {
          e.stopPropagation();
          menuActions.onToggle();
          e.preventDefault();
        }}
        isOpen={menuActions.isOpen}
        onClose={menuActions.onClose}
        triggerContent={
          <KTag
            color={requisitionStatusConfig[status]?.color}
            bg="white"
            borderColor="border-color"
            borderWidth="1px"
            value={<StatusContent showIcon={true} />}
          />
        }
        menuProps={{ gutter: 0 }}
        menuContentProps={{
          minW: shouldShowApprovalMenu ? '400px' : '360px',
          maxW: '320px',
          boxShadow: 'drop-shadows-bottom',
          zIndex: 1500,
          borderRadius: '6px',
          p: '0',
          onClick: (e: any) => {
            e.stopPropagation();
            e.preventDefault();
          },
        }}
        showRightIcon={false}
        isPortal={true}
        menuContent={
          <>
            {shouldShowApprovalMenu ? (
              <Box position="relative">
                {/* Close button/menu positioned absolutely */}
                {!isLoading && (
                  <>
                    {isPartOfGroup && allGroupRequisitions.length > 0 ? (
                      <Box
                        position="absolute"
                        top="8px"
                        right="8px"
                        zIndex={10}
                        onClick={(e: any) => {
                          e.stopPropagation();
                        }}
                      >
                        <KMenuButton
                          variant="secondary"
                          triggerContent="Close requisition"
                          menuContentProps={{
                            zIndex: 1600,
                            minW: '100px',
                            maxW: '320px',
                            borderRadius: '6px',
                            p: '0',
                          }}
                          dividerProps={{
                            display: 'none',
                          }}
                          menuContent={
                            <>
                              {closeMenuItems.map((item, index) => (
                                <MenuItem
                                  key={item.id}
                                  onClick={item.onClick}
                                  _hover={{
                                    fontWeight: 'semibold',
                                    bg: 'secondary-50',
                                  }}
                                  borderBottom={
                                    index !== closeMenuItems.length - 1
                                      ? '1px solid'
                                      : undefined
                                  }
                                  borderColor={
                                    index !== closeMenuItems.length - 1
                                      ? 'border-color'
                                      : undefined
                                  }
                                  p="12px !important"
                                  borderRadius="0"
                                  role="group"
                                  className="group"
                                >
                                  <Flex
                                    direction="column"
                                    gap="4px"
                                    flex="1"
                                    w="100%"
                                  >
                                    <Flex gap="6px" alignItems="center">
                                      <KText size="sm-medium" color="error-200">
                                        {item.label}
                                      </KText>
                                    </Flex>
                                    <KText
                                      size="sm-regular"
                                      color="secondary-600"
                                    >
                                      {item.description}
                                    </KText>
                                    {item.additionalInfo}
                                  </Flex>
                                </MenuItem>
                              ))}
                            </>
                          }
                        />
                      </Box>
                    ) : (
                      <Button
                        variant="secondary"
                        position="absolute"
                        top="8px"
                        right="8px"
                        zIndex={10}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (requisition) {
                            handleAction(
                              'close',
                              requisition,
                              menuActions.onClose
                            );
                          }
                        }}
                      >
                        Close requisition
                      </Button>
                    )}
                  </>
                )}

                {/* Approval Flow Steps */}
                <ApprovalFlowSteps
                  config={isRejected ? completedApprovalFlow : approvalFlow}
                  onClose={menuActions.onClose}
                  isLoading={isLoading}
                  offerId={requisitionId}
                  entityId={requisitionId}
                  entityType="requisition"
                  onSuccess={() => {
                    setHasFetched(false); // Reset to allow refetch
                    onApprovalChange?.();
                    menuActions.onClose();
                    if (requisition) {
                      onStatusChange?.('close');
                    }
                  }}
                  renderModalInParent={false}
                  handleModalOpen={() => {}}
                  handleUserSelect={() => {}}
                  applyToGroup={isPartOfGroup}
                  renderAdditionalInfo={
                    isPartOfGroup && allGroupRequisitions.length > 0
                      ? () => (
                          <GroupRequisitionCodes
                            requisitions={allGroupRequisitions}
                          />
                        )
                      : undefined
                  }
                />
              </Box>
            ) : (
              <>
                {actionMenuItems.map((action: any, index: number) => {
                  const isAllAction = action.isAllAction === true;
                  const baseActionId = isAllAction
                    ? (action.baseActionId as RequisitionActionType)
                    : action.id;

                  return (
                    <MenuItem
                      key={action.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (requisition) {
                          handleAction(
                            baseActionId,
                            requisition,
                            menuActions.onClose,
                            isAllAction,
                            isAllAction ? allGroupRequisitions : []
                          );
                        }
                      }}
                      color={action.color || 'secondary-900'}
                      borderBottom={
                        index !== actionMenuItems.length - 1 && '1px solid'
                      }
                      p="12px !important"
                      borderColor={
                        index !== actionMenuItems.length - 1 && 'border-color'
                      }
                      borderRadius="0"
                      role="group"
                      className="group"
                    >
                      <Flex direction="column" gap="3px" flex="1">
                        <Flex gap="6px" alignItems="center" w="100%">
                          {action.icon && (
                            <Icon
                              as={action.icon}
                              boxSize="16px"
                              color={action.color || 'secondary-700'}
                              flexShrink={0}
                            />
                          )}
                          <KText
                            size="sm-medium"
                            color={action.color || 'secondary-700'}
                          >
                            {action.label}
                          </KText>
                        </Flex>
                        <Flex direction="column" gap="6px">
                          {action.description && (
                            <KText size="sm-regular" color="secondary-700">
                              {action.description}
                            </KText>
                          )}
                          {isAllAction &&
                            action.groupRequisitionCodes &&
                            action.groupRequisitionCodes.length > 0 && (
                              <GroupRequisitionCodes
                                codes={action.groupRequisitionCodes}
                                codeStyles={{
                                  maxHeight: '200px',
                                  overflowY: 'auto',
                                }}
                              />
                            )}
                        </Flex>
                      </Flex>
                    </MenuItem>
                  );
                })}
              </>
            )}
          </>
        }
      />
      {/* Render confirmation modal from useRequisitionActions */}
      {renderModals()}
    </>
  );
};
